import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShoppingCart, Plus, Minus, X, Sparkles, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CheckoutDialog from '../components/CheckoutDialog';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Storefront = () => {
  const { vendorSlug } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [activeCat, setActiveCat] = useState('all');
  const [conflict, setConflict] = useState(null); // { product } pending add from a different store

  // Global, single-store cart (persists across the marketplace).
  const {
    items: cart,
    storeSlug: cartStoreSlug,
    storeName: cartStoreName,
    addItem,
    replaceWithItem,
    updateQuantity: cartUpdateQuantity,
    removeItem,
    totalPrice,
    totalCount,
  } = useCart();

  useEffect(() => {
    fetchStorefront();
  }, [vendorSlug]);

  useEffect(() => {
    if (store && !productsLoaded) {
      setTimeout(() => setProductsLoaded(true), 100);
    }
  }, [store]);

  const fetchStorefront = async () => {
    try {
      const response = await axios.get(`${API}/store/${vendorSlug}`);
      setStore(response.data);
    } catch (error) {
      toast.error('Store not found');
    } finally {
      setLoading(false);
    }
  };

  const storeRef = () => ({
    slug: vendorSlug,
    name: store?.vendor?.business_name || 'Store',
    currencySymbol: store?.vendor?.theme?.currency_symbol || '₹',
  });

  const addToCart = (product) => {
    const result = addItem(product, storeRef());
    if (result.conflict) {
      // Cart already holds items from another store — ask before replacing.
      setConflict({ product });
      return;
    }
    toast.success('Added to cart ✨', {
      description: `${product.name} added to your cart`,
    });
  };

  const confirmReplaceCart = () => {
    if (conflict) {
      replaceWithItem(conflict.product, storeRef());
      toast.success('Cart updated ✨', {
        description: `Started a new cart with ${conflict.product.name}`,
      });
    }
    setConflict(null);
  };

  const updateQuantity = (productId, delta) => cartUpdateQuantity(productId, delta);

  const removeFromCart = (productId) => {
    removeItem(productId);
    toast.success('Item removed');
  };

  const getTotalPrice = () => totalPrice;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto"></div>
            <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-purple-600 animate-pulse" />
          </div>
          <p className="mt-4 text-lg font-medium text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <p className="text-muted-foreground">Store not found</p>
      </div>
    );
  }

  const theme = store?.vendor?.theme || {};
  const currencySymbol = theme.currency_symbol || '₹';
  // Stock is only enforced when the store manages stock AND the item opts in.
  const manageStock = !!store?.vendor?.manage_stock;
  const isSoldOut = (p) => manageStock && p?.track_stock && (p?.stock ?? 0) <= 0;
  
  const getBackgroundImage = () => {
    if (theme.background_image) {
      return `url(${theme.background_image})`;
    }
    if (!theme.background_color || theme.background_color === '#FFFFFF' || theme.background_color.toLowerCase() === '#fff') {
      return `url("https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&h=1080&fit=crop&q=80")`;
    }
    return 'none';
  };

  const containerStyle = {
    backgroundColor: theme.background_color || '#FFFFFF',
    backgroundImage: getBackgroundImage(),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    fontFamily: theme.font_family || 'Inter, sans-serif',
    color: theme.font_color || '#000000',
  };
  
  // Detect dark backgrounds (any dark colour, not just pure black) so cards/text stay readable.
  const hexLuminance = (hex) => {
    if (!hex) return 1;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((ch) => ch + ch).join('');
    if (h.length !== 6) return 1;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const isDark = !theme.background_image && hexLuminance(theme.background_color) < 0.5;
  
  const headerStyle = {
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  };

  const storeName = store?.vendor?.business_name || 'Store';

  // Build menu sections from the order products appear in.
  const orderedCategories = [];
  store.products.forEach((p) => {
    const c = (p.category && p.category.trim()) || 'Menu';
    if (!orderedCategories.includes(c)) orderedCategories.push(c);
  });
  const hasCategories = orderedCategories.length > 1;
  const groupsToRender = activeCat === 'all' ? orderedCategories : [activeCat];

  // Featured "hero" items: one photographed dish per category, for the showcase strip.
  const featured = orderedCategories
    .map((c) => store.products.find((p) => ((p.category && p.category.trim()) || 'Menu') === c && p.image_url))
    .filter(Boolean)
    .slice(0, 6);

  const renderProductCard = (product, idx) => {
    const cartItem = cart.find((item) => item.product.id === product.id);
    const inCart = !!(cartItem && cartItem.quantity > 0);
    return (
      <Card
        key={product.id}
        className={`overflow-hidden product-card stagger-${Math.min((idx % 4) + 1, 4)}`}
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${theme.font_color || '#000'}20`,
          color: theme.font_color || '#000000',
        }}
      >
        {product.image_url && (
          <div className="relative overflow-hidden">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-40 sm:h-48 object-cover transition-transform duration-500 hover:scale-110"
            />
            <div className="absolute top-2 right-2">
              <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg text-[10px] sm:text-xs">
                {product.category}
              </Badge>
            </div>
          </div>
        )}
        <CardHeader className="p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-sm sm:text-lg leading-snug line-clamp-1" style={{ color: theme.font_color || '#000000' }}>
            {product.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <p className="text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2" style={{ color: theme.font_color || '#000000', opacity: 0.7 }}>
            {product.description}
          </p>
          <div className="flex justify-between items-center gap-2">
            <span className="text-lg sm:text-2xl font-extrabold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
              {currencySymbol}{product.price}
            </span>
            {isSoldOut(product) ? (
              <span className="text-xs text-muted-foreground">Sold out</span>
            ) : inCart ? (
              <div className="flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 p-1 shadow-md">
                <button
                  onClick={() => updateQuantity(product.id, -1)}
                  data-testid={`decrease-qty-card-${product.id}`}
                  className="h-7 w-7 rounded-full bg-white/20 text-white hover:bg-white/35 flex items-center justify-center transition-colors active:scale-90"
                  aria-label="Remove one"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="text-white font-bold text-sm w-6 text-center" data-testid={`qty-card-${product.id}`}>
                  {cartItem.quantity}
                </span>
                <button
                  onClick={() => updateQuantity(product.id, 1)}
                  data-testid={`increase-qty-card-${product.id}`}
                  className="h-7 w-7 rounded-full bg-white/20 text-white hover:bg-white/35 flex items-center justify-center transition-colors active:scale-90"
                  aria-label="Add one more"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <Button
                onClick={() => addToCart(product)}
                data-testid={`add-to-cart-${product.id}`}
                size="sm"
                className="btn-vibrant rounded-full shrink-0 h-9 sm:h-10 px-3 sm:px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md transition-all active:scale-95 hover:scale-105"
              >
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  const storeDescription = store?.products?.length
    ? `Shop from ${storeName}. ${store.products.length} products available. ${theme?.business_intro || 'Browse our collection and order online.'} Quick, no-login ordering.`
    : `Shop from ${storeName}. Browse our collection and order online. Quick, no-login ordering.`;
  const storeKeywords = store?.products?.length
    ? `${storeName}, ${[...new Set(store.products.map(p => p.category))].join(', ')}, online shopping, QR store`
    : `${storeName}, online shopping, QR store`;

  // og:image / schema images must be real http(s) URLs. Product/logo images here
  // are often base64 data-URIs (crawlers reject those), so pick the first valid
  // hosted URL and otherwise fall back to the site-wide card.
  // ponytail: data-URI images are a storage debt — real fix is hosting images off-DB.
  const httpImg = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u) ? u : null);
  const SITE_OG = 'https://saanjh.cc/og-image.jpg';
  const shareImage =
    httpImg(theme?.logo_url) ||
    httpImg(store?.products?.find((p) => httpImg(p.image_url))?.image_url) ||
    SITE_OG;
  // Razorpay/India default; map the symbol back to an ISO code for Offer schema.
  const CURRENCY_ISO = { '₹': 'INR', '$': 'USD', '€': 'EUR', '£': 'GBP' }[currencySymbol] || 'INR';
  const storeUrl = `https://saanjh.cc/store/${vendorSlug}`;
  const storeLd = JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: storeName,
      description: storeDescription,
      url: storeUrl,
      image: shareImage,
      ...(theme?.business_intro ? { slogan: theme.business_intro } : {}),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://saanjh.cc/' },
        { '@type': 'ListItem', position: 2, name: 'Stores', item: 'https://saanjh.cc/store' },
        { '@type': 'ListItem', position: 3, name: storeName, item: storeUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      numberOfItems: store?.products?.length || 0,
      itemListElement: (store?.products || []).slice(0, 50).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: p.name,
          ...(p.description ? { description: p.description } : {}),
          ...(httpImg(p.image_url) ? { image: p.image_url } : {}),
          ...(p.category ? { category: p.category } : {}),
          offers: {
            '@type': 'Offer',
            price: p.price,
            priceCurrency: CURRENCY_ISO,
            availability: isSoldOut(p)
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            url: storeUrl,
          },
        },
      })),
    },
  ]);

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>{storeName} - Online Store | SAANJH</title>
        <meta name="description" content={storeDescription} />
        <meta name="keywords" content={storeKeywords} />
        <link rel="canonical" href={storeUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${storeName} - Online Store | SAANJH`} />
        <meta property="og:description" content={theme?.business_intro || `Shop from ${storeName}. Browse our collection online.`} />
        <meta property="og:url" content={storeUrl} />
        <meta property="og:site_name" content="SAANJH" />
        <meta property="og:image" content={shareImage} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${storeName} - Online Store`} />
        <meta name="twitter:description" content={theme?.business_intro || `Shop from ${storeName}`} />
        <meta name="twitter:image" content={shareImage} />

        <script type="application/ld+json">{storeLd}</script>
      </Helmet>
      
      <div className="min-h-screen overflow-x-hidden relative" style={containerStyle} data-testid="storefront">

      {/* Vibrant decorative colour blobs (only on solid-colour stores so they don't clash with photo backgrounds) */}
      {!theme.background_image && (
        <div className="vibrant-blobs" aria-hidden="true">
          <span className="vibrant-blob b1" />
          <span className="vibrant-blob b2" />
          <span className="vibrant-blob b3" />
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 animate-slide-up" style={headerStyle}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center gap-3">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
            {theme.logo_url && (
              <img
                src={theme.logo_url}
                alt="Store Logo"
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-full animate-scale shrink-0"
                style={{ border: `2px solid ${theme.font_color || '#000'}40` }}
              />
            )}
            <div className="min-w-0">
              <button
                onClick={() => window.location.href = `/store/${vendorSlug}`}
                className="text-lg sm:text-2xl font-bold tracking-tight hover:opacity-80 transition-opacity cursor-pointer truncate max-w-[60vw] sm:max-w-none text-left block"
                data-testid="store-name"
                style={{ color: theme.font_color || '#000000' }}
              >
                {store.vendor.business_name}
              </button>
              {store.vendor.store_description && (
                <p 
                  className="text-sm mt-1 opacity-80"
                  style={{ color: theme.font_color || '#000000' }}
                  data-testid="store-description"
                >
                  {store.vendor.store_description}
                </p>
              )}
            </div>
          </div>
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className="relative transition-all hover:scale-110" 
                data-testid="cart-button"
                style={{
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: theme.font_color || '#000000',
                  border: `2px solid ${theme.font_color || '#000'}30`,
                }}
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.length > 0 && (
                  <span 
                    className="absolute -top-2 -right-2 rounded-full h-6 w-6 text-xs flex items-center justify-center font-bold animate-scale"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                    }}
                  >
                    {totalCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Shopping Cart</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-muted-foreground">Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    {cart.map((item) => (
                      <div key={item.product.id} className="flex gap-4 border-b pb-4 animate-slide-right">
                        {item.product.image_url && (
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-medium">{item.product.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {currencySymbol}{item.product.price}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product.id, -1)}
                              data-testid={`decrease-qty-${item.product.id}`}
                              className="h-7 w-7 p-0"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-sm font-medium w-8 text-center" data-testid={`qty-${item.product.id}`}>
                              {item.quantity}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product.id, 1)}
                              data-testid={`increase-qty-${item.product.id}`}
                              className="h-7 w-7 p-0"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFromCart(item.product.id)}
                              data-testid={`remove-${item.product.id}`}
                              className="ml-auto"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-lg font-bold mb-4">
                        <span>Total:</span>
                        <span data-testid="cart-total">{currencySymbol}{getTotalPrice().toFixed(2)}</span>
                      </div>
                      <Button
                        className="btn-vibrant w-full h-12 text-base rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg active:scale-95 transition-all"
                        onClick={() => {
                          setCartOpen(false);
                          setCheckoutOpen(true);
                        }}
                        data-testid="checkout-button"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Proceed to Checkout
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Hero Section with Business Intro */}
      {theme.business_intro && (
        <section className="hero-section relative z-10 py-10 sm:py-16 animate-slide-up">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div 
              className="p-8 rounded-3xl"
              style={{
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${theme.font_color || '#000'}15`,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Sparkles className="h-8 w-8 mx-auto mb-4 text-purple-600 animate-float" />
              <p
                className="text-lg leading-relaxed"
                style={{ color: theme.font_color || '#000000' }}
              >
                {theme.business_intro}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Hero showcase — featured dishes */}
      {featured.length >= 3 && (
        <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2 sm:pb-4 animate-slide-up">
          <div className="flex items-center gap-2.5 mb-4">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight" style={{ color: theme.font_color || '#000000' }}>
              Today's picks
            </h2>
            <Sparkles className="h-5 w-5 text-purple-600 animate-pulse" />
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-3 -mx-1 px-1 snap-x snap-mandatory">
            {featured.map((product) => (
              <div
                key={`hero-${product.id}`}
                className="group relative shrink-0 snap-start w-[80%] sm:w-[44%] md:w-[31%] lg:w-[23%] rounded-3xl overflow-hidden shadow-xl"
                style={{ border: `1px solid ${theme.font_color || '#000'}20` }}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="h-48 sm:h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <span className="absolute top-3 left-3 rounded-full bg-white/90 text-slate-900 text-[10px] font-bold px-2.5 py-1 shadow">
                  {product.category}
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-bold text-base sm:text-lg leading-tight line-clamp-1 drop-shadow">
                      {product.name}
                    </p>
                    <p className="text-white/90 font-extrabold text-lg sm:text-xl drop-shadow">
                      {currencySymbol}{product.price}
                    </p>
                  </div>
                  {(() => {
                    const heroItem = cart.find((i) => i.product.id === product.id);
                    const heroInCart = !!(heroItem && heroItem.quantity > 0);
                    if (isSoldOut(product)) return null;
                    if (heroInCart) return (
                      <div className="flex items-center gap-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 p-1">
                        <button
                          onClick={() => updateQuantity(product.id, -1)}
                          className="h-7 w-7 rounded-full bg-white/25 text-white hover:bg-white/40 flex items-center justify-center transition-colors active:scale-90"
                          aria-label="Remove one"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-white font-bold text-sm w-6 text-center">{heroItem.quantity}</span>
                        <button
                          onClick={() => updateQuantity(product.id, 1)}
                          className="h-7 w-7 rounded-full bg-white/25 text-white hover:bg-white/40 flex items-center justify-center transition-colors active:scale-90"
                          aria-label="Add one more"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    );
                    return (
                      <Button
                        onClick={() => addToCart(product)}
                        size="sm"
                        className="btn-vibrant rounded-full shrink-0 h-10 w-10 p-0 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg active:scale-90 transition-all"
                        aria-label={`Add ${product.name}`}
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Navigation */}
      {store.pages.length > 0 && (
        <nav className="border-b animate-slide-up relative z-10" style={headerStyle}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-4 sm:gap-6 overflow-x-auto">
            {store.pages.map((page, idx) => (
              <Link
                key={page.id}
                to={`/store/${vendorSlug}/page/${page.slug}`}
                className={`text-sm font-medium transition-all hover:scale-105 animate-slide-left stagger-${Math.min(idx + 1, 4)}`}
                style={{ 
                  color: theme.font_color || '#000000',
                  opacity: 0.8,
                }}
              >
                {page.title}
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Products */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center gap-2.5 mb-5 sm:mb-6 animate-slide-up">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: theme.font_color || '#000000' }}>
            Menu
          </h2>
          <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 animate-pulse" />
        </div>

        {/* Category filter bar */}
        {hasCategories && (
          <div
            className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-7 animate-slide-up"
            style={headerStyle}
          >
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {['all', ...orderedCategories].map((cat) => {
                const active = activeCat === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCat(cat)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-all active:scale-95 ${
                      active
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                        : 'hover:scale-105'
                    }`}
                    style={
                      active
                        ? undefined
                        : {
                            color: theme.font_color || '#000000',
                            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                            border: `1px solid ${theme.font_color || '#000'}20`,
                          }
                    }
                  >
                    {cat === 'all' ? 'All' : cat}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {store.products.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">No products available</p>
        ) : (
          groupsToRender.map((cat) => {
            const items = store.products.filter((p) => ((p.category && p.category.trim()) || 'Menu') === cat);
            if (!items.length) return null;
            return (
              <section key={cat} className="mb-9 sm:mb-12 animate-slide-up">
                <div className="flex items-baseline gap-3 mb-4 sm:mb-5">
                  <h3 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: theme.font_color || '#000000' }}>
                    {cat}
                  </h3>
                  <span className="text-xs sm:text-sm font-medium" style={{ color: theme.font_color || '#000000', opacity: 0.5 }}>
                    {items.length} item{items.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="product-grid">
                  {items.map((product, idx) => renderProductCard(product, idx))}
                </div>
              </section>
            );
          })
        )}
      </main>

      {/* Floating cart button (shows when cart has items) */}
      {cart.length > 0 && !cartOpen && (
        <button
          onClick={() => setCartOpen(true)}
          data-testid="floating-cart-button"
          aria-label="View cart"
          className="fixed z-50 flex items-center gap-3 rounded-full pl-5 pr-6 py-3.5 text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 active:scale-95 transition-all animate-scale"
          style={{
            right: 'max(1.25rem, env(safe-area-inset-right))',
            bottom: 'max(1.25rem, env(safe-area-inset-bottom))',
            boxShadow: '0 14px 34px rgba(124, 58, 237, 0.45)',
          }}
        >
          <span className="relative flex items-center">
            <ShoppingCart className="h-6 w-6" />
            <span className="absolute -top-2.5 -right-2.5 bg-white text-purple-700 text-[11px] font-extrabold rounded-full h-5 w-5 flex items-center justify-center shadow">
              {totalCount}
            </span>
          </span>
          <span className="font-bold whitespace-nowrap">{currencySymbol}{getTotalPrice().toFixed(2)}</span>
        </button>
      )}

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        cart={cart}
        vendorSlug={cartStoreSlug || vendorSlug}
        totalPrice={getTotalPrice()}
        currencySymbol={currencySymbol}
        vendor={store?.vendor}
      />

      {/* Cross-store cart guard: one cart belongs to one store at a time */}
      <AlertDialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <AlertDialogContent data-testid="cart-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new cart?</AlertDialogTitle>
            <AlertDialogDescription>
              Your cart already has items from <strong>{cartStoreName || 'another store'}</strong>. A
              cart can only contain items from one store. Clear it and add{' '}
              <strong>{conflict?.product?.name}</strong> from <strong>{storeName}</strong> instead?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cart-conflict-cancel">Keep current cart</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReplaceCart}
              data-testid="cart-conflict-confirm"
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
            >
              Clear &amp; add new
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Footer */}
      <footer
        className="border-t mt-16 relative z-10"
        style={{
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm" style={{ color: theme.font_color || '#000000', opacity: 0.6 }}>
            &copy; {new Date().getFullYear()} {store.vendor.business_name}. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: theme.font_color || '#000000', opacity: 0.6 }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
            <span>·</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms</a>
            <span>·</span>
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="hover:underline">Return &amp; Refund Policy</a>
          </div>
          <p className="text-xs mt-2" style={{ color: theme.font_color || '#000000', opacity: 0.5 }}>
            Powered by SAANJH · Saanjh, C/O ItsMeHere, SBS Nagar, Ludhiana, PB — 141001
          </p>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default Storefront;
